import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';

import { RepairServiceDetail } from './RepairServiceDetail';
import { ServiceDetail } from './ServiceDetail';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { ServiceTypeCode } from '../../../configs/enums';
import { useGetServiceById } from '../../../queries/service-query';
import { NotFoundError } from '../../Error/NotFound';

/**
 * Thin router that picks the detail layout from the service's job type:
 *   - Repair (serviceTypeCode === 'rep') → RepairServiceDetail
 *   - Every other job type (Support/Helpdesk, Install, Maintenance, …) → ServiceDetail
 *
 * Reads the code straight off the service so basic viewers without
 * service.edit.serviceParameters can still load the page. Both child components
 * call useServiceDetailData independently; React Query deduplicates the fetch.
 */
export const ServiceDetailRouter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const serviceId = id ? parseInt(id, 10) : 0;

  const serviceQuery = useGetServiceById(serviceId);

  if (serviceQuery.isLoading) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  // Guard here, before either detail layout mounts — they fire a dozen-plus
  // dependent queries (logs, allocations, billing, attributes, …) that would
  // all fail and retry against a job that doesn't exist.
  if (serviceQuery.isError || !serviceQuery.data) {
    return (
      <PageContainer>
        <NotFoundError title={id ? `Job ${id} not found` : 'Job not found'} message="" />
      </PageContainer>
    );
  }

  if (serviceQuery.data.serviceTypeCode === ServiceTypeCode.Repair) {
    return <RepairServiceDetail />;
  }

  return <ServiceDetail />;
};
